import type { ISwSettings } from "app/@types/ISwSettings";
import { getShopperUrl } from "app/common/hrefs";
import { handleGainAccessRedirect } from "app/common/utils/handleGainAccessRedirect";
import { allTrackers } from "app/services/track/track";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import {
    getDaysLeft,
    isAfterTrial,
    isAfterTrialCC,
    isPendingTrialStatus,
    isReactivatedInTrial,
    isSI,
    isTrialOrAfterTrial,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";

import { isShopperIcpEligible } from "./shopperIcpUtils";
import { type Banner, TRIAL_BANNER_TYPE } from "./trialBannerConfig";
import { getCTATextKey, shouldShowCTA } from "./trialBannerHelpers";

interface GetCTAButtonOnClickParams {
    bannerType: Banner;
    isSalesApp: boolean;
    isICPBanner: boolean;
}

export const getShouldShowBanner = (swSettings: ISwSettings) => {
    const isInTrialTest = GrowthbookService.growthbookInstance.isOn(TestsNames.TRIAL_BANNER);

    if (isPendingTrialStatus(swSettings) && !isInTrialTest) {
        return false;
    }
    return isPendingTrialStatus(swSettings) || isTrialOrAfterTrial(swSettings);
};

const getSIBannerType = (hasTrialEnded: boolean) => {
    if (hasTrialEnded) {
        return TRIAL_BANNER_TYPE.SI_AFTER_TRIAL;
    }
    return TRIAL_BANNER_TYPE.SI_TRIAL;
};

const getWebBannerType = (hasTrialEnded: boolean, swSettings: ISwSettings) => {
    if (hasTrialEnded) {
        if (isShopperIcpEligible(swSettings)) {
            return TRIAL_BANNER_TYPE.SHOPPER_ICP;
        }

        return isAfterTrialCC(swSettings) ? TRIAL_BANNER_TYPE.AFTER_TRIAL : TRIAL_BANNER_TYPE.TRIAL_ENDED;
    }

    if (isReactivatedInTrial(swSettings)) {
        return TRIAL_BANNER_TYPE.REACTIVATION;
    }

    if (isPendingTrialStatus(swSettings)) {
        return TRIAL_BANNER_TYPE.CC;
    }

    return TRIAL_BANNER_TYPE.TRIAL;
};

export const getBannerType = (swSettings: ISwSettings): Banner => {
    const isSIUser = isSI(swSettings);
    const hasTrialEnded = isAfterTrial(swSettings) && getDaysLeft(swSettings) < 1;

    if (isSIUser) {
        return getSIBannerType(hasTrialEnded);
    }

    return getWebBannerType(hasTrialEnded, swSettings);
};

const NULL_CTA = {
    ctaTextKey: null,
    ctaOnClick: null,
};

export const getCTAButtonOnClick = ({ bannerType, isSalesApp, isICPBanner }: GetCTAButtonOnClickParams) => {
    if (!shouldShowCTA(bannerType)) {
        return NULL_CTA;
    }

    const ctaTextKey = getCTATextKey(bannerType);
    let onClickFunction: (() => void) | null = null;

    if (!ctaTextKey) {
        return NULL_CTA;
    }

    if (isSalesApp) {
        onClickFunction = () =>
            handleGainAccessRedirect("hook", "click", "Internal Link", "TrialBanner", "Upgrade now");
    } else {
        if (isICPBanner) {
            onClickFunction = () => {
                allTrackers.dispatchTrackEvent("Internal Link", "click", "TrialBanner/TryOtherTrial/Shopper");
                window.open(getShopperUrl());
            };
        } else {
            onClickFunction = () => {
                const trackerName = `TrialBanner/${LocationService.getCurrentLocationTracking()}`;
                allTrackers.dispatchTrackEvent("Internal Link", "click", trackerName);
                goToPackagesPage();
            };
        }
    }

    return {
        ctaTextKey,
        ctaOnClick: onClickFunction,
    };
};
